/**
 * Token store - provides token retrieval for API requests.
 *
 * This module is used by useApi for token retrieval without importing authStore,
 * breaking the circular dependency chain.
 *
 * All token management is handled by Amplify - this module simply wraps
 * fetchAuthSession for use by the API interceptor.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

import { logger } from '@/utils/logger';

/**
 * Mutex to prevent concurrent token requests.
 */
let tokenPromise: Promise<string | null> | null = null;

/**
 * Reset the token promise mutex - for testing only
 */
export function __resetTokenPromise(): void {
  tokenPromise = null;
}

/**
 * Get a valid ID token for API requests.
 * Amplify handles token refresh automatically.
 */
export async function getValidToken(): Promise<string | null> {
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString() ?? null;
    } catch (err) {
      logger.error('Failed to get valid token:', err);
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}
